import { useEffect, useState } from "react";
import { Plus, PencilSimple, Trash, FloppyDisk, X, Copy, StackSimple, Eye } from "@phosphor-icons/react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import MarketingShell from "./MarketingShell";
import { SafeHtml } from "@/lib/safeHtml";

const CATEGORIES = [
  { v: "hero",        label: "Hero",         color: "#0055FF" },
  { v: "cta",         label: "CTA Button",   color: "#DC2626" },
  { v: "testimonial", label: "Testimonial",  color: "#7C3AED" },
  { v: "features",    label: "Feature Grid", color: "#16A34A" },
  { v: "divider",     label: "Divider",      color: "#A3A3A3" },
  { v: "footer",      label: "Footer",       color: "#525252" },
  { v: "other",       label: "Other",        color: "#0A0A0A" },
];

const catColor = v => (CATEGORIES.find(c => c.v === v) || CATEGORIES[CATEGORIES.length - 1]).color;
const catLabel = v => (CATEGORIES.find(c => c.v === v) || {}).label || v;

function BlockForm({ block, onCancel, onSaved }) {
  const [form, setForm] = useState({
    name:        block?.name || "",
    category:    block?.category || "other",
    description: block?.description || "",
    body_html:   block?.body_html || "",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.name.trim() || !form.body_html.trim()) {
      toast.error("Name and HTML body are required");
      return;
    }
    setSaving(true);
    try {
      if (block?.id) {
        await api.put(`/marketing/content-blocks/${block.id}`, form);
        toast.success("Block updated");
      } else {
        await api.post(`/marketing/content-blocks`, form);
        toast.success("Block created");
      }
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={onCancel}>
      <DialogContent className="rounded-none border-[#E5E5E5] max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-black text-xl tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            {block?.id ? "Edit" : "New"} Content Block
          </DialogTitle>
          <DialogDescription className="text-xs text-[#A3A3A3]">
            Reusable HTML snippets (hero, CTA, testimonial, divider, footer, …) that can be copy-pasted into any campaign's content step.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 pt-2">
          <div className="space-y-3">
            <div>
              <Label className="text-[10px] font-bold uppercase tracking-wider">Name</Label>
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="rounded-none mt-1 text-sm" data-testid="block-name-input" />
            </div>
            <div>
              <Label className="text-[10px] font-bold uppercase tracking-wider">Category</Label>
              <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} className="w-full border border-[#E5E5E5] h-10 px-3 mt-1 text-sm focus:outline-none focus:border-[#0055FF]" data-testid="block-category-select">
                {CATEGORIES.map(c => <option key={c.v} value={c.v}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-[10px] font-bold uppercase tracking-wider">Description</Label>
              <Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="rounded-none mt-1 text-sm" placeholder="Optional short note" />
            </div>
            <div>
              <Label className="text-[10px] font-bold uppercase tracking-wider">HTML Body</Label>
              <Textarea value={form.body_html} onChange={e => setForm({ ...form, body_html: e.target.value })} className="rounded-none mt-1 text-xs font-mono min-h-[280px]" data-testid="block-html-input" />
              <p className="text-[10px] text-[#A3A3A3] mt-1">
                Use inline CSS only. Supports <code>{"{{unsubscribe_url}}"}</code> and <code>{"{{preferences_url}}"}</code>.
              </p>
            </div>
          </div>
          <div>
            <Label className="text-[10px] font-bold uppercase tracking-wider">Live Preview</Label>
            <div className="border border-[#E5E5E5] mt-1 bg-white min-h-[280px] overflow-auto" data-testid="block-preview">
              {/* Live preview of the content block currently being edited.
                   SafeHtml runs DOMPurify on user-typed HTML before injection;
                   backend content_blocks.py also sanitises at write time. */}
              <SafeHtml
                className="p-3"
                data-testid="block-preview-body"
                html={form.body_html}
              />
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-4 border-t border-[#E5E5E5]">
          <Button variant="outline" className="rounded-none" onClick={onCancel}>Cancel</Button>
          <Button className="rounded-none bg-[#0055FF] hover:bg-[#003ECC] text-white" onClick={save} disabled={saving} data-testid="block-save-btn">
            <FloppyDisk size={14} className="mr-1.5" /> {saving ? "Saving…" : "Save Block"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function ContentBlocksPage() {
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCat, setFilterCat] = useState("");
  const [editing, setEditing] = useState(null);
  const [previewing, setPreviewing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(filterCat ? `/marketing/content-blocks?category=${filterCat}` : "/marketing/content-blocks");
      setBlocks(r.data || []);
    } catch { toast.error("Failed to load blocks"); }
    setLoading(false);
  };

  useEffect(() => { load(); }, [filterCat]);

  const duplicateBlock = async (b) => {
    try {
      await api.post(`/marketing/content-blocks`, {
        name: `${b.name} (copy)`,
        category: b.category,
        description: b.description,
        body_html: (await api.get(`/marketing/content-blocks/${b.id}`)).data.body_html,
      });
      toast.success("Duplicated");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Duplicate failed"); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/marketing/content-blocks/${confirmDelete.id}`);
      toast.success("Deleted");
      setConfirmDelete(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const copyHtml = async (b) => {
    try {
      const full = await api.get(`/marketing/content-blocks/${b.id}`);
      await navigator.clipboard.writeText(full.data.body_html || "");
      toast.success(`"${b.name}" HTML copied — paste into a campaign's HTML editor`);
    } catch { toast.error("Copy failed"); }
  };

  return (
    <MarketingShell>
    <div className="page-enter space-y-5" data-testid="content-blocks-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 text-[#0055FF] mb-1">
            <StackSimple size={16} />
            <span className="text-[10px] font-bold uppercase tracking-[0.25em]">Marketing</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tighter text-[#0A0A0A]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Content Blocks
          </h1>
          <p className="text-sm text-[#525252] mt-1">
            Reusable HTML snippets to drop into any campaign. {blocks.length} block{blocks.length !== 1 ? "s" : ""}.
          </p>
        </div>
        <Button onClick={() => setEditing({})} className="rounded-none bg-[#0A0A0A] hover:bg-[#262626] text-white" data-testid="new-block-btn">
          <Plus size={14} className="mr-1.5" /> New Block
        </Button>
      </div>

      {/* Category filter chips */}
      <div className="flex flex-wrap gap-1.5">
        <button onClick={() => setFilterCat("")} className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 border ${filterCat === "" ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white text-[#525252] border-[#E5E5E5] hover:border-[#0A0A0A]"}`}>
          All
        </button>
        {CATEGORIES.map(c => (
          <button
            key={c.v}
            onClick={() => setFilterCat(c.v)}
            className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 border transition-colors ${filterCat === c.v ? "text-white border-transparent" : "bg-white text-[#525252] border-[#E5E5E5] hover:border-[#0A0A0A]"}`}
            style={filterCat === c.v ? { backgroundColor: c.color, borderColor: c.color } : {}}
          >
            {c.label}
          </button>
        ))}
      </div>

      {/* Grid of blocks */}
      {loading ? (
        <div className="flex justify-center py-16"><div className="w-6 h-6 border-2 border-[#0055FF] border-t-transparent animate-spin" /></div>
      ) : blocks.length === 0 ? (
        <div className="border border-dashed border-[#E5E5E5] py-16 text-center">
          <StackSimple size={32} className="mx-auto text-[#A3A3A3] mb-3" />
          <p className="text-sm text-[#525252]">No blocks in this category yet.</p>
          <Button onClick={() => setEditing({})} variant="outline" className="rounded-none mt-3">
            <Plus size={14} className="mr-1.5" /> Create one
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {blocks.map(b => (
            <div key={b.id} className="border border-[#E5E5E5] bg-white hover:border-[#0055FF] transition-colors flex flex-col" data-testid={`block-${b.id}`}>
              {/* Category strip */}
              <div className="px-3 py-1.5 border-b border-[#E5E5E5] flex items-center justify-between">
                <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: catColor(b.category) }}>
                  {catLabel(b.category)}
                </span>
                <span className="text-[10px] text-[#A3A3A3] font-mono">{b.id.slice(-6)}</span>
              </div>
              {/* Body */}
              <div className="p-4 flex-1">
                <h3 className="text-sm font-bold text-[#0A0A0A] truncate" title={b.name}>{b.name}</h3>
                {b.description && <p className="text-xs text-[#525252] mt-1 line-clamp-2">{b.description}</p>}
              </div>
              {/* Actions */}
              <div className="border-t border-[#E5E5E5] p-2 flex items-center justify-end gap-1">
                <button onClick={() => setPreviewing(b)} className="p-1.5 text-[#525252] hover:text-[#0055FF] hover:bg-[#F0F7FF]" title="Preview" data-testid={`preview-${b.id}`}>
                  <Eye size={13} />
                </button>
                <button onClick={() => copyHtml(b)} className="p-1.5 text-[#525252] hover:text-[#0055FF] hover:bg-[#F0F7FF]" title="Copy HTML to clipboard" data-testid={`copy-${b.id}`}>
                  <Copy size={13} />
                </button>
                <button onClick={() => duplicateBlock(b)} className="p-1.5 text-[#525252] hover:text-[#0055FF] hover:bg-[#F0F7FF]" title="Duplicate">
                  <StackSimple size={13} />
                </button>
                <button onClick={() => setEditing(b)} className="p-1.5 text-[#525252] hover:text-[#0055FF] hover:bg-[#F0F7FF]" title="Edit" data-testid={`edit-${b.id}`}>
                  <PencilSimple size={13} />
                </button>
                <button onClick={() => setConfirmDelete(b)} className="p-1.5 text-[#DC2626] hover:bg-[#FEF2F2]" title="Delete">
                  <Trash size={13} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit form */}
      {editing && (
        <BlockForm
          block={editing}
          onCancel={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      {/* Preview modal */}
      {previewing && (
        <Dialog open onOpenChange={() => setPreviewing(null)}>
          <DialogContent className="rounded-none border-[#E5E5E5] max-w-2xl">
            <DialogHeader>
              <DialogTitle className="font-black tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                {previewing.name}
              </DialogTitle>
            </DialogHeader>
            <div className="border border-[#E5E5E5] bg-white max-h-[60vh] overflow-auto">
              <BlockPreviewBody id={previewing.id} />
            </div>
            <div className="flex justify-end gap-2 pt-3">
              <Button variant="outline" className="rounded-none" onClick={() => { copyHtml(previewing); setPreviewing(null); }}>
                <Copy size={13} className="mr-1.5" /> Copy HTML
              </Button>
              <Button className="rounded-none bg-[#0A0A0A] hover:bg-[#262626] text-white" onClick={() => setPreviewing(null)}>Close</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Delete confirm */}
      <Dialog open={!!confirmDelete} onOpenChange={() => setConfirmDelete(null)}>
        <DialogContent className="rounded-none border-[#E5E5E5] max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-black text-[#DC2626]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Delete Block?</DialogTitle>
            <DialogDescription className="text-xs text-[#A3A3A3]">
              This will permanently remove the block from the library.
            </DialogDescription>
          </DialogHeader>
          <p className="text-sm text-[#525252]">"{confirmDelete?.name}" will be permanently removed. Campaigns that already embedded this block's HTML will NOT be affected.</p>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" className="flex-1 rounded-none h-10" onClick={() => setConfirmDelete(null)}>Cancel</Button>
            <Button className="flex-1 rounded-none h-10 bg-[#DC2626] hover:bg-[#B91C1C] text-white font-bold" onClick={doDelete}>Delete</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
    </MarketingShell>
  );
}

function BlockPreviewBody({ id }) {
  const [html, setHtml] = useState("");
  useEffect(() => {
    api.get(`/marketing/content-blocks/${id}`).then(r => setHtml(r.data.body_html || ""));
  }, [id]);
  // Fetched body_html is the persisted, sanitize_rich_html-cleaned output
  // (see /app/backend/routes/marketing/content_blocks.py). SafeHtml also
  // runs DOMPurify at render time so legacy pre-hardening rows are
  // defensively cleaned on the way to the DOM.
  return <SafeHtml className="p-4" data-testid="content-block-preview" html={html} />;
}
